import { isPlatformBrowser } from "@angular/common";
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpRequest,
} from "@angular/common/http";
import { inject, PLATFORM_ID } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from "rxjs";
import { OmniarkModalService } from "../../shared/services/modal.service";
import { OmniarkToastService } from "../../shared/services/toast.service";
import { activeRoleIdOf } from "../../shared/utils/token-encoding.utils";
import { AuthService, RefreshTokenResponse } from "../services/auth.service";
import {
  BACKGROUND_REQUEST,
  ROLE_SWITCHED,
  SKIP_ERROR_TOAST,
} from "./request-context";

export { BACKGROUND_REQUEST, SKIP_ERROR_TOAST } from "./request-context";

let isRefreshing = false;
let sessionExpiredHandled = false;
// The access token a background request was last answered 401 with.
// Background requests are not sent again with that token; they flow again as
// soon as the tab holds a different one (a foreground refresh, role
// activation or a new login) — compared by value, so nothing has to clear it.
let rejectedBackgroundToken: string | null = null;
const refreshTokenSubject = new BehaviorSubject<string | false | null>(null);

const SESSION_EXPIRED_MODAL = {
  title: "Session Expired",
  message:
    "Your session has expired. You will be redirected to the login page.",
  primaryButtonText: "OK",
  primaryButtonVariant: "primary" as const,
};

const AUTHENTICATION_FAILED_MODAL = {
  title: "Authentication Failed",
  message: "Your authentication has failed. Please login again.",
  primaryButtonText: "OK",
  primaryButtonVariant: "primary" as const,
};

export function resetAuthInterceptorState(): void {
  isRefreshing = false;
  sessionExpiredHandled = false;
  rejectedBackgroundToken = null;
  refreshTokenSubject.next(null);
}

const ROLE_SWITCHED_MESSAGE =
  "Your role was switched in another tab. Nothing was saved. Check the page and try again.";
const ROLE_CHANGED_MESSAGE =
  "Your role changed while this was being sent. Nothing was saved. Check the page and try again.";

/** True when `token` acts under another role than the token `req` was sent
 *  with. After a switch in this tab the screen already shows the new role
 *  when the refresh answers, so comparing with the screen misses it. */
function sentUnderAnotherRole(
  req: HttpRequest<unknown>,
  token: string,
): boolean {
  const sentWith = req.headers.get("Authorization")?.replace(/^Bearer /, "");
  const sentUnder = activeRoleIdOf(sentWith);
  const replayUnder = activeRoleIdOf(token);
  return (
    sentUnder !== null && replayUnder !== null && sentUnder !== replayUnder
  );
}

/**
 * Send a request again with the refreshed token — unless it would write
 * under another role than the one it was made under.
 *
 * The access token is one per browser. A switch of role, in another tab or
 * in this one while a save is in flight, revokes the token the save was sent
 * with; the save gets a 401, and the refresh hands back a token for the NEW
 * role. Replaying the save with it would write to the new role's channel a
 * change the user made looking at the old one. Reads are replayed: the tab
 * follows the new role and reloads anyway.
 */
function replayWithToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  token: string,
  authService: AuthService,
  toastService: OmniarkToastService,
) {
  if (req.method === "GET" || !req.url.includes("/api/")) {
    return next(addTokenHeader(req, token));
  }
  let refusal: string | null = null;
  if (authService.actsUnderAnotherRole(token)) refusal = ROLE_SWITCHED_MESSAGE;
  else if (sentUnderAnotherRole(req, token)) refusal = ROLE_CHANGED_MESSAGE;
  if (!refusal) return next(addTokenHeader(req, token));
  if (!req.context.get(SKIP_ERROR_TOAST)) toastService.showError(refusal);
  return throwError(
    () =>
      new HttpErrorResponse({
        status: 409,
        statusText: ROLE_SWITCHED,
        url: req.url,
        error: { message: refusal },
      }),
  );
}

function addTokenHeader(request: HttpRequest<unknown>, token: string) {
  return request.clone({
    headers: request.headers.set("Authorization", `Bearer ${token}`),
  });
}

function showAuthEndModal(
  authService: AuthService,
  omniarkModal: OmniarkModalService,
  modal: typeof SESSION_EXPIRED_MODAL | typeof AUTHENTICATION_FAILED_MODAL,
): void {
  openAuthEndModal(omniarkModal, modal, (returnUrl) => {
    authService.logout(returnUrl);
  });
}

/**
 * Open the "Session Expired" dialog without a request having failed — for a
 * session found to have ended some other way (SessionExpiryService). Shares
 * the interceptor's once-only guard: whichever of the two gets there first
 * opens the dialog, and every later 401 fails fast. `onClose` gets the page
 * to come back to after the next login.
 */
export function showSessionExpiredModal(
  omniarkModal: OmniarkModalService,
  onClose: (returnUrl: string) => void,
): void {
  openAuthEndModal(omniarkModal, SESSION_EXPIRED_MODAL, onClose);
}

function openAuthEndModal(
  omniarkModal: OmniarkModalService,
  modal: typeof SESSION_EXPIRED_MODAL | typeof AUTHENTICATION_FAILED_MODAL,
  onClose: (returnUrl: string) => void,
): void {
  if (sessionExpiredHandled) {
    return;
  }
  sessionExpiredHandled = true;
  isRefreshing = false;
  refreshTokenSubject.next(false);

  // The session died on this page: the next login comes back to it.
  const returnUrl = window.location.pathname + window.location.search;
  omniarkModal.showModal(modal).subscribe({
    next: () => {
      onClose(returnUrl);
    },
    error: () => {
      onClose(returnUrl);
    },
  });
}

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  toastService: OmniarkToastService,
  omniarkModal: OmniarkModalService,
) {
  if (sessionExpiredHandled) {
    return throwError(
      () => new HttpErrorResponse({ status: 401, statusText: "Unauthorized" }),
    );
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response: RefreshTokenResponse) => {
        isRefreshing = false;
        const newAccessToken = response.data.accessToken;
        refreshTokenSubject.next(newAccessToken);
        return replayWithToken(
          req,
          next,
          newAccessToken,
          authService,
          toastService,
        );
      }),
      catchError((error) => {
        isRefreshing = false;
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            showAuthEndModal(authService, omniarkModal, SESSION_EXPIRED_MODAL);
          } else if (error.status === 403) {
            refreshTokenSubject.next(false);
            if (!req.context.get(SKIP_ERROR_TOAST)) {
              const errorMessage =
                error.error?.message ||
                "You do not have permission to perform this action";
              toastService.showError(errorMessage);
            }
          } else {
            refreshTokenSubject.next(false);
          }
        } else {
          refreshTokenSubject.next(false);
        }
        return throwError(() => error);
      }),
    );
  }

  return refreshTokenSubject.pipe(
    filter((token): token is string | false => token !== null),
    take(1),
    switchMap((token) => {
      if (token === false) {
        return throwError(() => new Error("Session expired"));
      }
      return replayWithToken(req, next, token, authService, toastService);
    }),
    catchError((err) => throwError(() => err)),
  );
}

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) {
  const authService = inject(AuthService);
  const toastService = inject(OmniarkToastService);
  const omniarkModal = inject(OmniarkModalService);
  const platformId = inject(PLATFORM_ID);
  const accessToken = authService.getAccessToken();

  // A request the user did not initiate (see BACKGROUND_REQUEST) never
  // refreshes the session: it is not sent at all without a token or with a
  // token a background request already saw rejected, and any error — a 401
  // included — goes straight to the caller. A 401 only records the token it
  // was sent with; it touches none of the refresh state, so the next
  // foreground request behaves exactly as if the background one had never
  // happened.
  if (req.context.get(BACKGROUND_REQUEST)) {
    if (!accessToken || accessToken === rejectedBackgroundToken) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: "Unauthorized",
            url: req.url,
          }),
      );
    }
    return next(addTokenHeader(req, accessToken)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          rejectedBackgroundToken = accessToken;
        }
        return throwError(() => error);
      }),
    );
  }

  if (accessToken) {
    req = addTokenHeader(req, accessToken);
  }

  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && isPlatformBrowser(platformId)) {
        if (
          (error.status === 401 || error.status === 500) &&
          req.url.includes("/auth/token/refresh")
        ) {
          showAuthEndModal(
            authService,
            omniarkModal,
            AUTHENTICATION_FAILED_MODAL,
          );
          return throwError(() => error);
        }

        if (error.status === 401) {
          return handle401Error(
            req,
            next,
            authService,
            toastService,
            omniarkModal,
          );
        }

        if (error.status === 403 && !req.context.get(SKIP_ERROR_TOAST)) {
          const errorMessage =
            error.error?.message ||
            "You do not have permission to perform this action";
          toastService.showError(errorMessage);
        }
      }

      return throwError(() => error);
    }),
  );
}
